import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import currency from 'currency.js';
import { useState } from 'react';

import { SingleOperators } from 'common/src/query-params/operators';
import { GENERATED_ROUTES } from 'common/src/routes/GeneratedRoutes';
import { DataFileStatus } from 'common/src/types/enums/DataFileStatus.enum';
import { IStatement } from 'common/src/types/interfaces';

import { ignoreStatement } from 'portal/src/api/StatementsService';
import useAuth from 'portal/src/auth/useAuth';
import useImgPreviewSrc from 'portal/src/components/billAutomation/FileUploadDetails/useImgPreviewSrc';
import TimestampDisplay from 'portal/src/components/common/TimestampDisplay';
import { BUTTON_TEXT } from 'portal/src/constants/button-text';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import {
  DataDisplay,
  DataFileTypeChip,
  QualityScoreDisplay,
  ValueDisplayType,
} from './components';
import ConfirmIgnoreDialog from './ConfirmIgnoreDialog';
import { getFileStatusChipColor } from './helpers';
import ImgPreviewContainer from './ImgPreviewContainer';

type DataDisplayType = {
  label: string;
  value: ValueDisplayType;
  direction?: 'row' | 'column';
};

const FileUploadDetails = ({
  entity,
  handleBackAction,
}: {
  entity?: IStatement;
  handleBackAction: () => void;
}) => {
  const { hasSystemAdminAccess, hasReadOnlyAccess } = useAuth();
  const snackbarHandler = useSnackbarHandler();
  const { previewSrc, isFetchingImg, isPdf, isError } = useImgPreviewSrc(
    entity.id,
  );
  const isSysAdmin = hasSystemAdminAccess();
  const isReadOnly = hasReadOnlyAccess();

  const [showIgnoreDialog, setShowIgnoreDialog] = useState(false);

  const { mutate: mutateIgnoreStatement, isPending: isIgnoringStatement } =
    useMutation({
      mutationFn: async (reason: string) => {
        await ignoreStatement(entity.id, reason);
      },
      onSuccess: () => {
        setShowIgnoreDialog(false);
        snackbarHandler.success('Statement ignored');
        handleBackAction();
      },
      onError: () => {
        snackbarHandler.error('Unable to ignore statement');
      },
    });

  const onDownloadClick = () => {
    if (entity.originalFileName && previewSrc) {
      const link = document.createElement('a');
      link.download = entity.originalFileName ?? 'download';
      link.href = previewSrc ?? '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      snackbarHandler.error('File not available for download');
    }
  };

  const onAssociatedBillsClick = () => {
    const filters = [
      {
        operator: SingleOperators.EQUALS,
        field: 'statementId',
        value: entity.id,
      },
    ];
    const url = `${GENERATED_ROUTES.ADMINISTRATION.BILLS}?filters=${JSON.stringify(filters)}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const summaryRows: DataDisplayType[] = [
    {
      label: 'Type',
      value: (
        <>
          <DataFileTypeChip label={entity.type} size="small" />
          <TimestampDisplay date={entity.uploadTimestamp} prefix="at " />
        </>
      ),
    },
  ];

  const metadata: DataDisplayType[] = [
    { label: 'Processed By', value: entity.processedBy },
    {
      label: 'Image Quality Score',
      value: <QualityScoreDisplay score={entity.imageQualityScore} />,
    },
    { label: 'Failure Reason', value: entity.failureReason },
  ];

  const discoveredData: DataDisplayType[] = [
    { label: 'Account Group', value: entity.accountGroup },
    { label: 'Utility', value: entity.utilityName },
    { label: 'Statement Date', value: entity.date },
    { label: 'Due Date', value: entity.dueDate },
    {
      label: 'Amount Due',
      value: entity.amountDue ? currency(entity.amountDue).format() : '-',
    },
    { label: 'Number of Pages', value: entity.numberOfPages },
  ];

  return (
    <Card variant="outlined" sx={{ p: 2, width: '100%' }}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h5" mb={0.5}>
            Upload Status:{' '}
          </Typography>
          <Chip
            label={entity.status}
            color={getFileStatusChipColor(entity.status)}
            size="small"
          />
          {entity.statusTimestamp && (
            <TimestampDisplay date={entity.statusTimestamp} prefix="at " />
          )}
        </Box>
        <Box display="flex" flexDirection="row" gap={1}>
          {entity.status !== DataFileStatus.Abandoned && (
            <>
              <Tooltip
                title={
                  entity.status === DataFileStatus.Processed
                    ? 'This statement has already been processed and cannot be irgnored. Contact support for assistance.'
                    : ''
                }
              >
                <span>
                  <Button
                    disabled={
                      isReadOnly ||
                      entity.status === DataFileStatus.Processed ||
                      isIgnoringStatement
                    }
                    color="error"
                    onClick={() => {
                      setShowIgnoreDialog(true);
                    }}
                    variant="outlined"
                  >
                    Ignore Statement
                  </Button>
                </span>
              </Tooltip>
              <Divider orientation="vertical" flexItem />
            </>
          )}
          {!isPdf && !isFetchingImg && !isError && (
            <>
              <Button
                color="secondary"
                onClick={onDownloadClick}
                variant="contained"
              >
                {BUTTON_TEXT.download}
              </Button>
              <Divider orientation="vertical" flexItem />
            </>
          )}
          <Button color="info" onClick={handleBackAction} variant="outlined">
            {BUTTON_TEXT.back}
          </Button>
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        gap={2}
      >
        <Card
          variant="outlined"
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            width: '30%',
          }}
        >
          <Box display="flex" flexDirection="column">
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={() => {
                  if (entity.originalFileName) {
                    navigator.clipboard.writeText(entity.originalFileName);
                    snackbarHandler.info('File name copied');
                  }
                }}
              >
                <ContentCopyIcon />
              </IconButton>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ wordBreak: 'break-word' }}
                mt={1}
              >
                {entity.originalFileName}
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={1} px={1}>
              {summaryRows.map((row) => (
                <DataDisplay
                  key={row.label}
                  label={row.label}
                  value={row.value}
                  direction={row.direction}
                />
              ))}
            </Box>
            {isSysAdmin && (
              <>
                <Divider sx={{ mt: 3, mb: 1 }}>Metadata</Divider>
                <Box display="flex" flexDirection="column" gap={1} px={1}>
                  {metadata.map((row) => (
                    <DataDisplay
                      key={row.label}
                      label={row.label}
                      value={row.value}
                      direction={row.direction}
                    />
                  ))}
                </Box>
              </>
            )}
            {!isSysAdmin &&
              (entity.status === DataFileStatus.Abandoned ||
                entity.status === DataFileStatus.ClientReviewRequired) && (
                <>
                  <Divider sx={{ mt: 3, mb: 1 }}>Metadata</Divider>
                  <DataDisplay
                    label="Failure Reason"
                    value={entity.failureReason ?? '-'}
                    direction="row"
                  />
                </>
              )}
            <Divider sx={{ mt: 3, mb: 1 }}>Discovered Data</Divider>
            <Box display="flex" flexDirection="column" mb={2} gap={1} px={1}>
              {discoveredData.map((row) => (
                <DataDisplay
                  key={row.label}
                  label={row.label}
                  value={row.value}
                  direction={row.direction}
                />
              ))}
              <Button
                color="secondary"
                onClick={onAssociatedBillsClick}
                variant="contained"
                disabled={!entity.bills || entity.bills.length === 0}
                sx={{ mt: 2, justifyContent: 'space-between' }}
              >
                Associated Bills ({entity.bills?.length ?? 0}){' '}
                <OpenInNewIcon fontSize="small" />
              </Button>
            </Box>
          </Box>
        </Card>
        <Box display="flex" width="70%">
          <ImgPreviewContainer
            previewSrc={previewSrc}
            isFetchingImg={isFetchingImg}
            isPdf={isPdf}
            addBackground
          />
        </Box>
      </Box>
      <ConfirmIgnoreDialog
        open={showIgnoreDialog}
        isLoading={isIgnoringStatement}
        onClose={() => {
          if (!isIgnoringStatement) {
            setShowIgnoreDialog(false);
          }
        }}
        onCancel={() => {
          setShowIgnoreDialog(false);
        }}
        onConfirm={(reason) => {
          mutateIgnoreStatement(reason);
        }}
      />
    </Card>
  );
};

export default FileUploadDetails;
